import { useEffect, useMemo, useState } from "react";
import type { RatioLadder } from "../../../../utils/models";

/** The ladder's ids sorted by "Census from", the order it settles into. Ties keep their order in the ladder. */
export const sortedLadderIds = (ladder: readonly RatioLadder[]): string[] => [...ladder].sort((a, b) => a.censusStart - b.censusStart).map((step) => step.id);

/**
 * The steps in the order they are shown: the settled order for the steps it already has, then any step added since,
 * in the ladder's own order (a new step is appended, so it shows where "Add a step" put it). A removed step simply drops out.
 */
export const ladderInDisplayOrder = (ladder: readonly RatioLadder[], settledIds: readonly string[]): RatioLadder[] => {
	const byId = new Map(ladder.map((step) => [step.id, step]));
	const settled = new Set(settledIds);
	const kept = settledIds.flatMap((id) => {
		const step = byId.get(id);
		return step === undefined ? [] : [step];
	});
	return [...kept, ...ladder.filter((step) => !settled.has(step.id))];
};

/**
 * A ratio ladder's steps in display order (both the v1 and v2 lists). They open sorted by "Census from", but an edit doesn't move them: the
 * order re-sorts only once the ladder has gone `delayMs` without a change, so the step being typed in stays put.
 */
export const useSettledLadderOrder = (ladder: readonly RatioLadder[], delayMs: number): RatioLadder[] => {
	const [settledIds, setSettledIds] = useState<string[]>(() => sortedLadderIds(ladder));

	useEffect(() => {
		const timer = window.setTimeout(() => setSettledIds(sortedLadderIds(ladder)), delayMs);
		return () => window.clearTimeout(timer);
	}, [ladder, delayMs]);

	return useMemo(() => ladderInDisplayOrder(ladder, settledIds), [ladder, settledIds]);
};
